import { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import { Registry } from "types";
import DashboardCreateSelectGatewayModal from "../create/modal/SelectGateways";
import {
    ContainerStyled,
    useCreateDashboardStore,
} from "components/dashboard/create";
import { PanelV2 } from "generated/models";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { getWidgetConfigFromChartSource } from "../utils/widgetConfig";

const getInitialSelectedGateways = (panel: any, allRegistry: any[]) => {
    const initialGateways: Record<string, any> = {};

    (panel?.attributes || []).forEach((attr: any) => {
        const gateway = (allRegistry || []).find(
            (registry: any) => registry.gateway_id === attr.gateway_id
        );
        if (gateway) {
            initialGateways[gateway.gateway_id] = gateway;
        }
    });

    return initialGateways;
};

const UpdateCombinedGatewayPanel = ({
    onChange,
    panel,
}: {
    onChange: (field: string, value: any) => void;
    panel: PanelV2;
}) => {
    const [selectModal, setSelectModal] = useState<any>(null);
    const { allRegistry } = useCreateDashboardStore();

    const [selectedGateways, setSelectedGateways] =
        useState<Record<string, Registry>>();

    useEffect(() => {
        const gateways = getInitialSelectedGateways(panel, allRegistry);
        setSelectedGateways(gateways);
    }, [panel, allRegistry]);

    const widgetConfiguration = getWidgetConfigFromChartSource(panel.chart, panel.source);

    const onGatewayChange = (gateways: Record<string, any>) => {
        const attributes = Object.values(gateways).map((gateway: any) => ({
            gateway_id: gateway.gateway_id,
        }));
        setSelectedGateways(gateways);
        handleChangeFormData("attributes", attributes);
    };

    const handleChangeFormData = (field: string, value: any) => {
        if (typeof onChange === "function") {
            onChange(field, value);
        }
    };

    return (
        <ContainerStyled>
            <Form.Group>
                <Form.Group>
                    <Form.Label>Widget name</Form.Label>
                    <Form.Control
                        name="name"
                        type="text"
                        placeholder={
                            widgetConfiguration?.defaultWidgetName ?? ""
                        }
                        onChange={(e) =>
                            handleChangeFormData("name", e.target.value)
                        }
                        defaultValue={panel?.name ?? ""}
                    />
                </Form.Group>

                <div className="d-flex align-items-center gap-3">
                    <div>
                        <Button
                            variant="primary"
                            onClick={() => setSelectModal(true)}
                        >
                            Select Gateways
                        </Button>
                    </div>

                    {!!widgetConfiguration?.maxItems && (
                        <div style={{ fontSize: 14, color: "#828FAB" }}>
                            (Max {widgetConfiguration?.maxItems} Gateways)
                        </div>
                    )}
                </div>

                <GatewayList
                    selectedGateways={selectedGateways ?? {}}
                    onChange={onGatewayChange}
                />

                <DashboardCreateSelectGatewayModal
                    show={selectModal}
                    onHide={() => setSelectModal(false)}
                    values={selectedGateways}
                    onSave={(e, gateways) => onGatewayChange(gateways)}
                />
            </Form.Group>
        </ContainerStyled>
    );
};

const GatewayList = ({ selectedGateways, onChange }: any) => {
    const handleRemoveGateway = (gateway: any) => {
        const updatedGateways = { ...selectedGateways };
        delete updatedGateways[gateway.gateway_id];
        onChange(updatedGateways);
    };

    return (
        <Row className="mt-4 cstm-table">
            <Col sm={12}>
                <Form id="chart-edit-form" onChange={() => { }}>
                    {!!Object.values(selectedGateways).length && (
                        <div className="table-head" key="head">
                            <Row className="no-checkbox">
                                <Col>Gateway Name</Col>
                                <Col>UUID</Col>
                                <Col md={{ span: 1 }}></Col>
                            </Row>
                        </div>
                    )}

                    {Object.values(selectedGateways).map((gateway: any) => (
                        <div
                            key={gateway.gateway_id}
                            id={gateway.gateway_id}
                            className="table-row gateway-chart-info"
                        >
                            <div className="desktop-chart-info">
                                <Row className="no-checkbox">
                                    <Col>{gateway.name}</Col>
                                    <Col>{gateway.gateway_id}</Col>
                                    <Col
                                        md={{ span: 1 }}
                                        className="d-flex justify-content-end"
                                    >
                                        <CloseOutlined
                                            className="cursor-pointer mr-2 text-primary-red-1"
                                            onClick={() =>
                                                handleRemoveGateway(gateway)
                                            }
                                            onKeyDown={() =>
                                                handleRemoveGateway(gateway)
                                            }
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    ))}
                </Form>
            </Col>
        </Row>
    );
};

export default UpdateCombinedGatewayPanel;
